import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import customerPortalAdminService from '../../services/customerPortalAdminService';

/**
 * Invite a new customer (super-admin). Minimal info + at least one login
 * identifier. The temp password is delivered to the customer via
 * invited_channels — never shown here. Surfaces the channel outcome and the
 * "no channel enabled" warning.
 */
const CustomerInviteModal = ({ isOpen, onClose, onInvited }) => {
  const { t } = useTranslation();
  const s = t('customers.invite', { returnObjects: true });

  const [form, setForm] = useState({ owner_name: '', company_name: '', customer_type: 'Customer', login_email: '', login_mobile: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { invited_channels, throttled_channels, customer_id }

  useEffect(() => {
    if (isOpen) {
      setForm({ owner_name: '', company_name: '', customer_type: 'Customer', login_email: '', login_mobile: '' });
      setError(''); setResult(null); setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const update = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (error) setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.login_email.trim() && !form.login_mobile.trim()) {
      setError(s?.errors?.identifierRequired || 'Enter a login email or mobile.');
      return;
    }
    const body = {
      owner_name: form.owner_name.trim() || undefined,
      company_name: form.company_name.trim() || undefined,
      customer_type: form.customer_type,
    };
    if (form.login_email.trim()) body.login_email = form.login_email.trim();
    if (form.login_mobile.trim()) body.login_mobile = form.login_mobile.trim();

    setError('');
    setSubmitting(true);
    try {
      const res = await customerPortalAdminService.invite(body);
      if (res.success) {
        setResult(res.data);
        onInvited?.(res.data);
      } else {
        setError(res.error || (s?.errors?.failed) || 'Could not send the invite.');
      }
    } catch (err) {
      setError((s?.errors?.failed) || 'Could not send the invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };

  // Success summary: which channels the invite actually went out on.
  const invited = result?.invited_channels || [];
  const throttled = result?.throttled_channels || [];
  const noChannel = result && invited.length === 0 && throttled.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.sectionTitle || 'Portal onboarding'}</p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'Invite customer'}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:scale-105 transition" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1) }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="px-6 py-6">
          {result ? (
            // Post-invite summary
            <div className="space-y-3">
              {noChannel ? (
                <div className="rounded-xl px-4 py-3 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1), color: UI_COLORS.STATUS.WARNING, border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.3)}` }}>
                  {s?.noChannel || 'No email/WhatsApp channel is enabled, so the customer received nothing. Enable a channel and use Resend from the customer’s Portal access card.'}
                </div>
              ) : (
                <div className="rounded-xl px-4 py-3 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1), color: UI_COLORS.STATUS.SUCCESS, border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3)}` }}>
                  {(s?.sentVia || 'Invite sent via')} {invited.length ? invited.join(', ') : '—'}.
                </div>
              )}
              {throttled.length > 0 && (
                <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  {(s?.throttledNote || 'Skipped (sent very recently):')} {throttled.join(', ')}
                </p>
              )}
              <div className="flex justify-end pt-2">
                <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold" style={{ background: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                  {s?.done || 'Done'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.note || 'The customer gets a temporary password by email/WhatsApp, changes it, and completes their profile for approval.'}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.fields?.company || 'Company name'}</label>
                  <input type="text" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.fields?.owner || 'Owner name'}</label>
                  <input type="text" value={form.owner_name} onChange={(e) => update('owner_name', e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.fields?.type || 'Type'}</label>
                <select value={form.customer_type} onChange={(e) => update('customer_type', e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle}>
                  <option value="Customer">{s?.types?.customer || 'Customer'}</option>
                  <option value="Supplier">{s?.types?.supplier || 'Supplier'}</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.fields?.email || 'Login email'}</label>
                  <input type="email" value={form.login_email} onChange={(e) => update('login_email', e.target.value)} placeholder="owner@example.com" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.fields?.mobile || 'Login mobile'}</label>
                  <input type="text" value={form.login_mobile} onChange={(e) => update('login_mobile', e.target.value)} placeholder="9876543210" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
                </div>
              </div>
              <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.identifierHint || 'At least one of email or mobile is required.'}</p>

              {error && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{error}</div>}

              <div className="flex justify-end gap-3 pt-2 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <button type="button" onClick={onClose} disabled={submitting} className="px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50" style={{ color: UI_COLORS.TEXT.MUTED, border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}` }}>
                  {s?.cancel || 'Cancel'}
                </button>
                <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-60" style={{ background: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                  <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                  {submitting ? (s?.sending || 'Sending…') : (s?.submit || 'Send invite')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerInviteModal;
